import React, {
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import styled from "styled-components";
import BannerMessage from "./BannerMessage";
import { AuthContext } from "../../utils/AuthContext";
import { useBannerStore } from "../../stores/bannerStore";
import { useFetchBannerMessages } from "../../hooks/use-fetch-banner-messages";
import { normalizeMessages, sortMessages } from "../../utils/bannerMessages";

const POLL_INTERVAL_MS = 5 * 60 * 1000;
const RETRY_INTERVAL_MS = 30 * 1000;
const OPERATOR_RESTORE_MS = 60 * 1000;
const FAILURES_BEFORE_DEGRADED = 2;
const POLL_TIMEOUT_MS = 25 * 1000;
const MIN_POLL_GAP_MS = 5 * 1000;

export const DEGRADED_ID = "banner-degraded";
export const DEGRADED_PRIORITY = 2;
export const DEGRADED_MESSAGE =
  "We're having trouble reaching RTD backend services. Information on this screen may be out of date.";

const VIEW = {
  DEFAULT: "default",
  EXPANDED: "expanded",
};

// Capped so a long stack can never squeeze a virtualized table down to zero.
const Banner = styled.section`
  width: 100%;
  box-sizing: border-box;
  max-height: 40vh;
  background: #ffffff;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  min-width: 0;
  min-height: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
`;

const MessageBanner = () => {
  const auth = useContext(AuthContext) || {};
  const operatorView = Boolean(auth.operatorView);
  const fetchBannerMessages = useFetchBannerMessages();

  const dismissed = useBannerStore((state) => state.dismissed);
  const dismiss = useBannerStore((state) => state.dismiss);
  const restore = useBannerStore((state) => state.restore);
  const setSummary = useBannerStore((state) => state.setSummary);

  const [messages, setMessages] = useState([]);
  const [degraded, setDegraded] = useState(false);
  const [view, setView] = useState(VIEW.DEFAULT);
  const [liveReady, setLiveReady] = useState(false);

  const listId = useId();
  const bannerRef = useRef(null);
  const failuresRef = useRef(0);
  const seenUrgentRef = useRef(new Set());
  const baselineRef = useRef(false);
  const viewTouchedRef = useRef(false);

  // A manual view change forfeits the first-load baseline (AC 8).
  const changeView = useCallback((next) => {
    viewTouchedRef.current = true;
    setView(next);
  }, []);

  // Returns the delay before the next poll; degraded state needs two failures.
  const applyResponse = useCallback((response) => {
    if (response?.success) {
      const normalized = normalizeMessages(response.data);
      // The first load is the baseline; only P1s that appear later force-expand.
      if (!baselineRef.current) {
        baselineRef.current = true;
        if (!viewTouchedRef.current) {
          normalized
            .filter((message) => message.priority === 1)
            .forEach(({ id }) => seenUrgentRef.current.add(id));
        }
      }
      failuresRef.current = 0;
      setDegraded(false);
      setMessages(normalized);
      return POLL_INTERVAL_MS;
    }

    const unauthorized = response?.code === 401 || response?.status === 401;
    const notDeployed = response?.status === 404;
    const gatewayFailure = response?.code === 500 || response?.code === 501;

    // Neither a 401 nor a 404 is a gateway failure; both break the run.
    if (unauthorized || notDeployed || !gatewayFailure) {
      failuresRef.current = 0;
      // A 401 says nothing about upstream health, so it may not retract.
      if (!unauthorized) setDegraded(false);
      return POLL_INTERVAL_MS;
    }

    failuresRef.current += 1;
    if (failuresRef.current >= FAILURES_BEFORE_DEGRADED) {
      setDegraded(true);
      return POLL_INTERVAL_MS;
    }
    return RETRY_INTERVAL_MS;
  }, []);

  useEffect(() => {
    let cancelled = false;
    let inFlight = false;
    let timerId;
    let lastPollStartedAt = 0;

    const schedule = (delay) => {
      globalThis.clearTimeout(timerId);
      timerId = globalThis.setTimeout(safePoll, delay);
    };

    // A never-settling request would otherwise pin inFlight and kill polling.
    const withTimeout = (promise) => {
      let timeoutId;
      return Promise.race([
        promise,
        new Promise((_, reject) => {
          timeoutId = globalThis.setTimeout(
            () => reject(new Error("banner request timed out")),
            POLL_TIMEOUT_MS,
          );
        }),
      ]).finally(() => globalThis.clearTimeout(timeoutId));
    };

    // One request at a time; overlapping rounds corrupt the failure count.
    async function poll() {
      if (cancelled || inFlight || document.hidden) return;
      inFlight = true;
      lastPollStartedAt = Date.now();
      let delay;
      try {
        const response = await withTimeout(
          Promise.resolve().then(fetchBannerMessages),
        );
        if (cancelled) return;
        delay = applyResponse(response);
      } catch {
        if (cancelled) return;
        delay = applyResponse({ success: false, code: 500 });
      } finally {
        inFlight = false;
      }
      if (!cancelled) schedule(delay);
    }

    // poll() is fire-and-forget, so a rejection escaping it would end the loop.
    const safePoll = () => {
      poll().catch(() => {
        inFlight = false;
        if (!cancelled) schedule(RETRY_INTERVAL_MS);
      });
    };

    const onVisibilityChange = () => {
      if (document.hidden) {
        globalThis.clearTimeout(timerId);
        return;
      }
      // Rapid alt-tabbing must not fire back-to-back rounds.
      const sinceLast = Date.now() - lastPollStartedAt;
      if (sinceLast < MIN_POLL_GAP_MS) {
        schedule(MIN_POLL_GAP_MS - sinceLast);
        return;
      }
      safePoll();
    };

    safePoll();
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      cancelled = true;
      globalThis.clearTimeout(timerId);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [fetchBannerMessages, applyResponse]);

  const activeMessages = useMemo(() => sortMessages(messages), [messages]);

  // The degraded row goes in last, so the stable sort puts it after other P2s.
  const rows = useMemo(() => {
    if (!degraded) return activeMessages;
    return sortMessages([
      ...activeMessages,
      {
        id: DEGRADED_ID,
        message: DEGRADED_MESSAGE,
        priority: DEGRADED_PRIORITY,
      },
    ]);
  }, [activeMessages, degraded]);

  // A P1 whose content hash has not been seen this session forces the banner open.
  useEffect(() => {
    const urgent = activeMessages.filter((message) => message.priority === 1);
    const arrived = urgent.some(({ id }) => !seenUrgentRef.current.has(id));
    urgent.forEach(({ id }) => seenUrgentRef.current.add(id));
    if (arrived) {
      setView(VIEW.EXPANDED);
      restore();
    }
  }, [activeMessages, restore]);

  // Nobody is at a kiosk to reopen a collapsed banner, so it reopens itself.
  useEffect(() => {
    if (!operatorView) return undefined;
    if (view !== VIEW.DEFAULT && !dismissed) return undefined;
    const timeoutId = globalThis.setTimeout(() => {
      setView(VIEW.EXPANDED);
      restore();
    }, OPERATOR_RESTORE_MS);
    return () => globalThis.clearTimeout(timeoutId);
  }, [operatorView, view, dismissed, restore]);

  const hasContent = rows.length > 0;
  const expanded = view === VIEW.EXPANDED;

  // Collapsed still shows every P1 and the degraded row; never fewer than one.
  const alwaysVisible = useMemo(() => {
    const urgent = rows.filter(
      ({ priority, id }) => priority === 1 || id === DEGRADED_ID,
    );
    return urgent.length > 0 ? urgent : rows.slice(0, 1);
  }, [rows]);

  const visibleRows = expanded ? rows : alwaysVisible;
  const hiddenCount = rows.length - alwaysVisible.length;
  const canToggle = hiddenCount > 0 || expanded;

  const toggleExpanded = useCallback(
    () => changeView(expanded ? VIEW.DEFAULT : VIEW.EXPANDED),
    [expanded, changeView],
  );

  // The Navbar pill reads this, so it stays accurate while the banner is hidden.
  useEffect(() => {
    setSummary(rows.length, rows[0]?.priority ?? null);
  }, [rows, setSummary]);

  const visibleKey = visibleRows
    .map(({ id, message }) => `${id}:${message}`)
    .join("|");

  // The live region has to exist before its first content or nothing is announced.
  // Dismissal unmounts it, so restoring has to take the same two-commit route.
  useEffect(() => {
    setLiveReady(hasContent && !dismissed);
  }, [hasContent, dismissed]);

  // Keeps the virtualized tables from overflowing the viewport.
  useEffect(() => {
    const root = document.documentElement;
    const element = bannerRef.current;
    if (!element) {
      root.style.removeProperty("--banner-height");
      return undefined;
    }

    const write = () =>
      root.style.setProperty(
        "--banner-height",
        `${Math.round(element.getBoundingClientRect().height)}px`,
      );
    write();

    if (!globalThis.ResizeObserver) {
      return () => root.style.removeProperty("--banner-height");
    }
    const observer = new globalThis.ResizeObserver(write);
    observer.observe(element);
    return () => {
      observer.disconnect();
      root.style.removeProperty("--banner-height");
    };
  }, [hasContent, dismissed, view, liveReady, visibleKey]);

  if (!hasContent || dismissed) return null;

  return (
    <Banner ref={bannerRef} aria-label="Application messages">
      <List id={listId} aria-live="polite">
        {liveReady &&
          visibleRows.map(({ id, message, priority }, index) => (
            <BannerMessage
              key={id}
              message={message}
              priority={priority}
              truncate={!expanded}
              hiddenCount={index === 0 ? hiddenCount : 0}
              expanded={index === 0 ? expanded : false}
              onToggle={index === 0 && canToggle ? toggleExpanded : null}
              onDismiss={index === 0 ? dismiss : null}
            />
          ))}
      </List>
    </Banner>
  );
};

export default MessageBanner;
